import { inject, Injectable, signal } from '@angular/core';
import { SnapshotService } from '../snapshot/snapshot.service';
import { WarehouseStateSnapshotMeta, WarehouseStateSnapshotPayload } from '../snapshot/warehouse-state-snapshot.dto';

export interface SnapshotSessionKey {
  divisionId: string;
  snapshotId: string;
}

/**
 * Holds the one snapshot payload currently being viewed, in memory only.
 *
 * Deliberately never written to IndexedDB, in online and offline builds alike: historical state
 * needs a connection, is lazily fetched, and dies with the page.
 *
 * One slot, not a cache: payloads are whole-warehouse and the mobile target cannot afford an LRU.
 */
@Injectable()
export class SnapshotSessionStore {
  private readonly snapshots = inject(SnapshotService);

  // Key and payload are replaced together, as one unit, so they can never disagree.
  private key: SnapshotSessionKey | null = null;
  private payload: WarehouseStateSnapshotPayload | null = null;
  private meta: WarehouseStateSnapshotMeta | null = null;

  private inflightKey: SnapshotSessionKey | null = null;
  // Latest requested snapshot; what requireLoaded() resolves to.
  private requestedKey: SnapshotSessionKey | null = null;
  private inflight: Promise<void> | null = null;
  // Bumped by every load() and by clear(); a fetch whose attempt no longer matches lost the race and discards its result.
  private latestLoadAttempt = 0;

  readonly loaded = signal<SnapshotSessionKey | null>(null);

  async load(divisionId: string, snapshotId: string): Promise<void> {
    this.requestedKey = { divisionId, snapshotId };
    if (this.isLoaded({ divisionId, snapshotId })) {
      // Supersede any other in-flight load so it can't overwrite this one.
      this.latestLoadAttempt++;
      this.inflight = null;
      this.inflightKey = null;
      return;
    }

    if (this.inflight !== null && sameKey(this.inflightKey, { divisionId, snapshotId })) {
      return this.inflight;
    }

    const key = { divisionId, snapshotId };
    const attempt = ++this.latestLoadAttempt;
    const request = this.fetch(divisionId, snapshotId, attempt).finally(() => {
      if (this.inflight === request) {
        this.inflight = null;
        this.inflightKey = null;
      }
    });
    this.inflightKey = key;
    this.inflight = request;

    return request;
  }

  /**
   * Guarded at read time, not just at write time: a payload captured under one division can never
   * be rendered under another, even if nothing cleared it on the way out.
   */
  require(divisionId: string): WarehouseStateSnapshotPayload {
    if (!this.payload || this.key?.divisionId !== divisionId) {
      throw new Error(`No snapshot payload loaded for division: ${divisionId}`);
    }

    return this.payload;
  }

  /** require(), after waiting for any in-flight load for this division. */
  async requireLoaded(divisionId: string): Promise<WarehouseStateSnapshotPayload> {
    let failure: unknown = null;
    for (let request = this.pendingFor(divisionId); request !== null; request = this.pendingFor(divisionId)) {
      failure = await request.then(
        () => null,
        (error: unknown) => error,
      );
    }

    // Only the latest load matters; superseded failures are ignored.
    const requested = this.requestedKey;
    if (requested?.divisionId === divisionId && this.isLoaded(requested)) {
      return this.payload;
    }

    throw failure ?? new Error(`No snapshot payload loaded for division: ${divisionId}`);
  }

  /** Non-throwing requireLoaded() for one key: null if failed or superseded. */
  async whenLoaded(key: SnapshotSessionKey): Promise<WarehouseStateSnapshotPayload | null> {
    if (this.inflight !== null && sameKey(this.inflightKey, key)) {
      await this.inflight.catch((): void => undefined);
    }

    return this.payloadIfLoaded(key);
  }

  metaFor(divisionId: string): WarehouseStateSnapshotMeta | null {
    return this.key?.divisionId === divisionId ? this.meta : null;
  }

  /** Non-throwing counterpart of require(), for callers that fall back to live data. */
  payloadIfLoaded(key: SnapshotSessionKey): WarehouseStateSnapshotPayload | null {
    return this.isLoaded(key) ? this.payload : null;
  }

  isLoaded(key: SnapshotSessionKey): boolean {
    return !!this.payload && sameKey(this.key, key);
  }

  activeKey(): SnapshotSessionKey | null {
    return this.key;
  }

  clear(): void {
    this.latestLoadAttempt++;
    this.requestedKey = null;
    this.key = null;
    this.payload = null;
    this.meta = null;
    this.inflightKey = null;
    this.inflight = null;
    this.loaded.set(null);
  }

  private pendingFor(divisionId: string): Promise<void> | null {
    return this.inflightKey?.divisionId === divisionId ? this.inflight : null;
  }

  private async fetch(divisionId: string, snapshotId: string, attempt: number): Promise<void> {
    const [meta, payload] = await Promise.all([this.snapshots.getSnapshotMeta(snapshotId), this.snapshots.getSnapshotPayload(snapshotId)]);
    if (attempt !== this.latestLoadAttempt) {
      return;
    }

    // Wholesale replacement — no merging, so nothing survives from a previous snapshot.
    this.key = { divisionId, snapshotId };
    this.payload = payload;
    this.meta = meta;
    this.loaded.set(this.key);
  }
}

function sameKey(a: SnapshotSessionKey | null, b: SnapshotSessionKey | null): boolean {
  return !!a && !!b && a.divisionId === b.divisionId && a.snapshotId === b.snapshotId;
}
